import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { DEALS, STAGE_MAP, dateFromDaysAgo, type Deal } from './crmData'
import { InitialBox, ScoreBadge, StageBadge, Pill, FlagChips, crmToast, CrmToaster, ConfirmDialog, IconCalendarUi, IconPhone, IconTrash, IconUpload, Overlay, SearchInput, TrialDiscoveryBadge } from './crmUi'
import { useCrm } from './useCrm'
import { getDeals, archiveDeal, archiveDeals, restoreDeal, addActivity } from './crmApi'
import { Button } from '../shared/Button'
import { QuickBookButton } from './BookingModal'
import { DealQualification, QualPctBadge, QualStatusChip } from './DealQualification'
import { CrmDuplicates } from './CrmDuplicates'
import { getDuplicates } from './crmApi'
import { useAuth } from '../../contexts/AuthContext'
import { LeadKebab } from './LeadKebab'
import { TouchDots } from './DealTouchpoints'
import { getTouchpointsSummary, type TouchSummary } from './crmApi'
import { matchesTzFilter, setTzFilter, useTzFilter } from './tzFilter'

/**
 * Leads — a tabbed table view over the Board's deals (the single source of
 * truth). Uploading a CSV/Excel adds every contact as a Cold Lead. Tabs bucket
 * the same deals by lifecycle; each row shows a Demo / No-demo indicator drawn
 * from the deal's scheduled demo. Clicking a row opens the full deal detail.
 *
 * Admins can archive (soft-delete) leads — per row, or many at once with the
 * multi-select checkboxes — and restore them from the Archived tab. Archiving
 * hides a lead from every list, board and metric but keeps it recoverable.
 */

// ── lifecycle buckets — SAME taxonomy as the board and Customer Success. Event/Active leads are
// Leads-page extras (uploaded/event leads and demo-active leads outside the trial lifecycle). ─────
const hasDemo = (d: Deal) => d.demoScheduledDays != null
const isTrialExpired = (d: Deal) => d.stage === 'free_trial_expired'
const isTrialExpiredContacted = (d: Deal) => d.stage === 'trial_expired_contacted'
const isCancelled = (d: Deal) => ['cancelled_subscription', 'closed_lost', 'archived'].includes(d.stage)
const isLive = (d: Deal) => d.stage === 'successfully_signed_up'
const TRIAL_LIFECYCLE = ['onboarding_in_progress', 'onboarding_complete', 'trial_reactivated']
// Onboarded = WEQUOTE's half finished (explicit status). Customer-finished stays Free Trial.
const isOnboarded = (d: Deal) => TRIAL_LIFECYCLE.includes(d.stage) && d.onboardingStatus === 'wequote_finished'
const isEventLead = (d: Deal) => ['auto_new_lead', 'qualification'].includes(d.stage) && !hasDemo(d) && d.trialStartedDays == null
const isActiveLead = (d: Deal) => hasDemo(d) && !isLive(d) && !isTrialExpired(d) && !isTrialExpiredContacted(d) && !isCancelled(d) && d.trialStartedDays == null && !['onboarding_in_progress', 'onboarding_complete', 'trial_reactivated'].includes(d.stage)
const isFreeTrial = (d: Deal) =>
  (TRIAL_LIFECYCLE.includes(d.stage) && d.onboardingStatus !== 'wequote_finished') ||
  (['auto_new_lead', 'qualification'].includes(d.stage) && d.trialStartedDays != null)

// To Qualify (PRD v1.0, Lee 7 Aug 2026): every UNQUALIFIED pre-trial lead with no
// demo booked — the cold-calling worklist. Qualified/Disqualified/Nurture drop out
// as reps work it; the call button dials the contact and forces the outcome form.
const isToQualify = (d: Deal) =>
  ['auto_new_lead', 'qualification'].includes(d.stage) &&
  d.trialStartedDays == null &&
  !hasDemo(d) &&
  !['qualified', 'disqualified', 'nurture'].includes(d.qualificationStatus || 'not_qualified')

type TabKey = 'event' | 'to_qualify' | 'active' | 'free_trial' | 'onboarded' | 'trial_expired' | 'trial_expired_contacted' | 'live' | 'cancelled' | 'disqualified' | 'duplicates' | 'archived'
interface TabDef {
  key: TabKey
  label: string
  match: (d: Deal, meId?: string) => boolean
}
const TABS: TabDef[] = [
  { key: 'event', label: 'Event Leads', match: isEventLead },
  { key: 'to_qualify', label: 'To Qualify', match: isToQualify },
  { key: 'active', label: 'Active Leads', match: isActiveLead },
  { key: 'free_trial', label: 'Free Trial Sign Up', match: isFreeTrial },
  { key: 'onboarded', label: 'Onboarded On-Trial', match: isOnboarded },
  { key: 'trial_expired', label: 'Trial Expired', match: isTrialExpired },
  { key: 'trial_expired_contacted', label: 'Trial Expired — Contacted', match: isTrialExpiredContacted },
  { key: 'live', label: 'Live', match: isLive },
  { key: 'cancelled', label: 'Cancelled/Churned', match: isCancelled },
  // Disqualified sign-ups in one place (Lee, 7 Aug 2026) — PRD status, any stage.
  { key: 'disqualified', label: 'Disqualified', match: (d) => d.qualificationStatus === 'disqualified' },
  // Possible duplicates live on their own tab (Phase 2.10), not mixed into the lead list.
  { key: 'duplicates', label: 'Possible Duplicates', match: () => false },
  // Archived (soft-deleted) leads — admin only; shown at the end.
  { key: 'archived', label: 'Archived', match: () => false },
]

const DemoCell: React.FC<{ deal: Deal }> = ({ deal }) => {
  if (deal.demoScheduledDays == null) return <Pill tone="gray">No demo</Pill>
  const future = deal.demoScheduledDays < 0
  return (
    <Pill tone={future ? 'green' : 'blue'}>
      <IconCalendarUi className="w-3 h-3" /> Demo {future ? `in ${Math.abs(deal.demoScheduledDays)}d` : dateFromDaysAgo(deal.demoScheduledDays)}
    </Pill>
  )
}

// How many rows each tab shows at once.
const PAGE_SIZE = 50

// Pull the whole list across pages (the API caps a single request at 1000). Used for both the
// active list and the archived list so tabs count/paginate over the FULL set, not a first slice.
const fetchAllDeals = async (params: Record<string, any> = {}): Promise<Deal[]> => {
  const size = 500
  const all: Deal[] = []
  for (let offset = 0; offset < 100000; offset += size) {
    const r = await getDeals({ ...params, limit: size, offset })
    const batch = (r.deals as Deal[]) || []
    all.push(...batch)
    if (batch.length < size) break
  }
  return all
}

export const CrmLeads: React.FC = () => {
  const navigate = useNavigate()
  const { currentAgent } = useAuth()
  const meId = currentAgent?.id
  const isAdmin = currentAgent?.role === 'admin'
  const { data: deals, reload } = useCrm<Deal[]>(() => fetchAllDeals({ includeTerminal: true }), DEALS)

  const [tab, setTab] = useState<TabKey>('event')
  const [q, setQ] = useState('')
  const [qualFilter, setQualFilter] = useState<'all' | 'not_qualified' | 'working' | 'qualified' | 'disqualified' | 'nurture'>('all')
  // To Qualify tab: All Leads vs By Event (the show/event they were captured at).
  const [qualView, setQualView] = useState<'all' | 'event'>('all')
  const [eventSel, setEventSel] = useState('')
  // The qualification dialog. forced=true after a call — it cannot be dismissed
  // until at least one outcome save lands (PRD: the call MUST be logged).
  const [qualDialog, setQualDialog] = useState<{ deal: Deal; forced: boolean } | null>(null)
  const [qualSaves, setQualSaves] = useState(0)
  const [page, setPage] = useState(0)
  const [showFilter, setShowFilter] = useState('')
  // Clock-wall timezone filter — shared with the strip in the top banner.
  const tzFilter = useTzFilter()
  const [touch, setTouch] = useState<Record<string, TouchSummary>>({})
  useEffect(() => { getTouchpointsSummary().then(setTouch).catch(() => {}) }, [deals])
  const [dupCount, setDupCount] = useState(0)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [confirm, setConfirm] = useState<null | { title: string; message: React.ReactNode; onConfirm: () => Promise<void> }>(null)
  // Archived leads are a separate query (the main list excludes them). Loaded on demand.
  const [archived, setArchived] = useState<Deal[]>([])
  const loadArchived = () => fetchAllDeals({ archived: true }).then(setArchived).catch(() => setArchived([]))

  useEffect(() => {
    getDuplicates().then((r) => setDupCount(r.clusters.length)).catch(() => setDupCount(0))
  }, [deals])
  useEffect(() => { if (tab === 'archived') loadArchived() }, [tab])
  // Clear selection and jump back to page 1 whenever the tab or search changes.
  useEffect(() => { setSelected(new Set()); setPage(0) }, [tab])
  useEffect(() => { setPage(0) }, [q])

  const counts = useMemo(() => {
    const c: Record<TabKey, number> = { event: 0, to_qualify: 0, active: 0, free_trial: 0, onboarded: 0, trial_expired: 0, trial_expired_contacted: 0, live: 0, cancelled: 0, disqualified: 0, duplicates: 0, archived: 0 }
    for (const d of deals) for (const t of TABS) if (t.match(d, meId)) c[t.key]++
    return c
  }, [deals, meId])

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase()
    const source = tab === 'archived' ? archived : deals.filter((d) => TABS.find((t) => t.key === tab)!.match(d, meId))
    let list = source
    if (needle) list = list.filter((d) => `${d.name} ${d.company} ${d.contact} ${d.source}`.toLowerCase().includes(needle))
    // PRD v1.0 qualification filter — matches the server-computed status.
    if (qualFilter !== 'all') list = list.filter((d) => (d.qualificationStatus || 'not_qualified') === qualFilter)
    // To Qualify → By Event view: only the selected show's captures.
    if (tab === 'to_qualify' && qualView === 'event' && eventSel) list = list.filter((d) => (d.opportunity || '(no event)') === eventSel)
    // Global show/event filter (Lee, 17 Sep 2026) — works on every tab.
    if (showFilter) list = list.filter((d) => (d.opportunity || '') === showFilter)
    // Clock-wall timezone filter (Lee, 23 Sep 2026) — a selected clock narrows here too.
    if (tzFilter) list = list.filter((d) => matchesTzFilter(tzFilter, d.localTz))
    return [...list].sort((a, b) => (a.createdDays ?? 0) - (b.createdDays ?? 0))
  }, [deals, archived, tab, q, meId, qualFilter, qualView, eventSel, showFilter, tzFilter])

  // Every show/event that has captured leads, with counts — the global filter.
  const allShows = useMemo(() => {
    const m = new Map<string, number>()
    for (const d of deals) if (d.opportunity) m.set(d.opportunity, (m.get(d.opportunity) || 0) + 1)
    return [...m.entries()].sort((a, b) => b[1] - a[1])
  }, [deals])

  // Events present in the To Qualify pool, with counts — the By Event picker.
  const qualEvents = useMemo(() => {
    if (tab !== 'to_qualify') return []
    const m = new Map<string, number>()
    for (const d of deals) if (isToQualify(d)) { const k = d.opportunity || '(no event)'; m.set(k, (m.get(k) || 0) + 1) }
    return [...m.entries()].sort((a, b) => b[1] - a[1])
  }, [deals, tab])

  // Pagination — 50 per page, per tab.
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE))
  useEffect(() => { if (page > pageCount - 1) setPage(pageCount - 1) }, [pageCount, page])
  const clampedPage = Math.min(page, pageCount - 1)
  const pageRows = useMemo(() => rows.slice(clampedPage * PAGE_SIZE, clampedPage * PAGE_SIZE + PAGE_SIZE), [rows, clampedPage])
  const from = rows.length === 0 ? 0 : clampedPage * PAGE_SIZE + 1
  const to = Math.min(rows.length, (clampedPage + 1) * PAGE_SIZE)

  const toggleOne = (id: string) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n })
  // "Select all" applies to the rows visible on THIS page.
  const allShownSelected = pageRows.length > 0 && pageRows.every((d) => selected.has(d.id))
  const toggleAll = () => setSelected((s) => {
    const n = new Set(s)
    if (allShownSelected) pageRows.forEach((d) => n.delete(d.id))
    else pageRows.forEach((d) => n.add(d.id))
    return n
  })

  const doArchiveOne = (d: Deal) => setConfirm({
    title: `Archive “${d.company || d.name}”?`,
    message: <>It will be hidden from every list, board and metric, but you can restore it from the <b>Archived</b> tab.</>,
    onConfirm: async () => {
      await archiveDeal(d.id)
      crmToast('Lead archived', 'success')
      setSelected((s) => { const n = new Set(s); n.delete(d.id); return n })
      reload(); if (tab === 'archived') loadArchived()
    },
  })
  const doArchiveSelected = () => {
    const ids = [...selected]
    setConfirm({
      title: `Archive ${ids.length} lead${ids.length === 1 ? '' : 's'}?`,
      message: <>They’ll be hidden from every list, board and metric. You can restore them from the <b>Archived</b> tab.</>,
      onConfirm: async () => {
        const r = await archiveDeals(ids)
        crmToast(`${r.archived} lead${r.archived === 1 ? '' : 's'} archived`, 'success')
        setSelected(new Set())
        reload()
      },
    })
  }
  const doRestore = async (d: Deal) => {
    await restoreDeal(d.id)
    crmToast('Lead restored', 'success')
    loadArchived(); reload()
  }

  const isArchivedTab = tab === 'archived'
  const showChecks = isAdmin && !isArchivedTab

  return (
    <div className="h-full flex flex-col bg-surface-secondary">
      <CrmToaster />

      {/* Header */}
      <div className="px-6 pt-6 pb-4 bg-surface border-b border-border flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-txt-primary">Leads</h1>
          {tzFilter && (
            <button
              onClick={() => setTzFilter(null)}
              className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-brand-50 text-brand-700 border border-brand-300 hover:bg-brand-100"
              title="Clear the timezone filter"
            >
              {tzFilter.label} time only ✕
            </button>
          )}
        </div>
        <Button size="sm" onClick={() => navigate('/crm/leads/import')}>
          <IconUpload className="w-3.5 h-3.5" /> Import leads
        </Button>
      </div>

      {/* Tabs */}
      <div className="px-6 pt-3 bg-surface border-b border-border flex items-center gap-1 overflow-x-auto">
        {TABS.filter((t) => t.key !== 'archived' || isAdmin).map((t) => {
          const active = t.key === tab
          const badge = t.key === 'duplicates' ? dupCount : t.key === 'archived' ? archived.length : counts[t.key]
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`relative px-3.5 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${
                active ? 'border-brand-500 text-brand-600' : 'border-transparent text-txt-secondary hover:text-txt-primary'
              }`}
            >
              {t.label}
              {(t.key !== 'archived' || archived.length > 0) && (
                <span className={`ml-2 text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${active ? 'bg-brand-100 text-brand-700' : 'bg-surface-tertiary text-txt-tertiary'}`}>
                  {badge}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* Content */}
      {tab === 'duplicates' ? (
        /* Possible duplicates get the whole tab to themselves (Phase 2.10). */
        <div className="flex-1 min-h-0 overflow-auto p-6">
          <CrmDuplicates onMerged={reload} />
          {dupCount === 0 && (
            <div className="rounded-xl border border-dashed border-border bg-surface p-12 text-center">
              <p className="text-sm text-txt-secondary">No possible duplicates found.</p>
              <p className="text-[11px] text-txt-tertiary mt-1">Certain matches (same account or email), likely (same name) and possible (similar names) would appear here.</p>
            </div>
          )}
        </div>
      ) : (
      <div className="flex-1 min-h-0 flex flex-col">
        {/* Pinned: search + count (or the bulk-action bar when rows are selected). */}
        {selected.size > 0 ? (
          <div className="px-6 pt-4 pb-3 flex items-center justify-between gap-3 shrink-0">
            <p className="text-sm font-medium text-txt-primary">{selected.size} selected</p>
            <div className="flex items-center gap-2">
              <button onClick={() => setSelected(new Set())} className="px-3 py-2 text-sm font-medium rounded-lg text-txt-secondary hover:bg-surface-hover">Clear</button>
              <button onClick={doArchiveSelected} className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-semibold rounded-lg text-white bg-red-600 hover:bg-red-700"><IconTrash className="w-3.5 h-3.5" /> Archive selected</button>
            </div>
          </div>
        ) : (
          <div className="px-6 pt-4 pb-3 flex items-center justify-between gap-3 shrink-0">
            <div className="flex items-center gap-2 min-w-0 flex-wrap">
              <SearchInput value={q} onChange={setQ} placeholder="Search name, company, contact, source…" className="w-72 max-w-full" />
              {/* Global show/event filter (Lee, 17 Sep 2026) — every tab, not just To Qualify. */}
              {allShows.length > 0 && (
                <select
                  value={showFilter}
                  onChange={(e) => { setShowFilter(e.target.value); setPage(0) }}
                  className={`rounded-lg border bg-surface px-2 py-2 text-xs font-medium focus:outline-none max-w-[240px] ${showFilter ? 'border-brand-500 text-brand-600' : 'border-border text-txt-primary'}`}
                  title="Only leads captured at this show/event"
                >
                  <option value="">All shows/events</option>
                  {allShows.map(([ev, n]) => <option key={ev} value={ev}>{ev} ({n})</option>)}
                </select>
              )}
              {tab === 'to_qualify' && (
                <>
                  <div className="flex rounded-lg border border-border overflow-hidden">
                    {([['all', 'All Leads'], ['event', 'By Event']] as const).map(([k, label]) => (
                      <button
                        key={k}
                        onClick={() => { setQualView(k); setPage(0) }}
                        className={`px-3 py-2 text-xs font-semibold ${qualView === k ? 'bg-brand-600 text-white' : 'bg-surface text-txt-secondary hover:bg-surface-hover'}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  {qualView === 'event' && (
                    <select
                      value={eventSel}
                      onChange={(e) => { setEventSel(e.target.value); setPage(0) }}
                      className="rounded-lg border border-border bg-surface px-2 py-2 text-xs font-medium text-txt-primary focus:outline-none max-w-[240px]"
                    >
                      <option value="">— pick an event —</option>
                      {qualEvents.map(([ev, n]) => <option key={ev} value={ev}>{ev} ({n})</option>)}
                    </select>
                  )}
                </>
              )}
              <select
                value={qualFilter}
                onChange={(e) => { setQualFilter(e.target.value as typeof qualFilter); setPage(0) }}
                className="rounded-lg border border-border bg-surface px-2 py-2 text-xs font-medium text-txt-primary focus:outline-none"
                title="Filter by qualification status (server-computed)"
              >
                <option value="all">Qualification: all</option>
                <option value="qualified">Qualified</option>
                <option value="working">Working</option>
                <option value="not_qualified">Not Qualified</option>
                <option value="nurture">Nurture</option>
                <option value="disqualified">Disqualified</option>
              </select>
            </div>
            <p className="text-xs text-txt-tertiary">{rows.length === 0 ? '0' : `${from}–${to}`} of {rows.length}</p>
          </div>
        )}

        {/* Only the rows scroll; the table header stays pinned. */}
        <div className="flex-1 min-h-0 overflow-auto px-6 pb-6">
        <div className="rounded-xl border border-border bg-surface overflow-hidden">
          {rows.length === 0 ? (
            <div className="py-16 text-center">
              <p className="text-sm text-txt-secondary">{isArchivedTab ? 'Nothing archived.' : 'No leads in this tab.'}</p>
              {tab === 'event' && (
                <div className="mt-3 inline-flex">
                  <Button size="sm" onClick={() => navigate('/crm/leads/import')}>
                    <IconUpload className="w-3.5 h-3.5" /> Import your first leads
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10">
                <tr className="text-left text-[11px] uppercase tracking-wide text-txt-tertiary border-b border-border bg-surface-secondary">
                  {showChecks && (
                    <th className="py-2.5 pl-4 pr-1 w-8">
                      <input type="checkbox" checked={allShownSelected} onChange={toggleAll} aria-label="Select all shown" />
                    </th>
                  )}
                  <th className="py-2.5 px-4 font-medium">Lead</th>
                  <th className="py-2.5 px-3 font-medium" title="Touchpoint health — LinkedIn › Mobile › Email › Mailing list › Telephone">Reach</th>
                  <th className="py-2.5 px-3 font-medium">Stage</th>
                  <th className="py-2.5 px-3 font-medium">Qualification</th>
                  <th className="py-2.5 px-3 font-medium">Demo</th>
                  <th className="py-2.5 px-3 font-medium">Score</th>
                  <th className="py-2.5 px-3 font-medium">Source</th>
                  <th className="py-2.5 px-3 font-medium">Added</th>
                  <th className="py-2.5 px-4 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((d) => (
                  <tr
                    key={d.id}
                    onClick={() => !isArchivedTab && navigate(`/crm/lead/${d.id}`)}
                    className={`border-b border-border last:border-0 align-middle ${isArchivedTab ? '' : 'hover:bg-surface-hover cursor-pointer'} ${selected.has(d.id) ? 'bg-brand-50/40' : ''}`}
                  >
                    {showChecks && (
                      <td className="py-2.5 pl-4 pr-1" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={selected.has(d.id)} onChange={() => toggleOne(d.id)} aria-label={`Select ${d.company || d.name}`} />
                      </td>
                    )}
                    <td className="py-2.5 px-4">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <InitialBox name={d.company || d.name} logoUrl={d.logoUrl} />
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <p className="font-medium text-txt-primary truncate">{d.company || d.name}</p>
                            <TrialDiscoveryBadge trialStartedAt={d.trialStartedAt} discoveryCallDone={d.discoveryCallDone} stage={d.stage} />
                            <FlagChips flags={d.flags} />
                          </div>
                          <p className="text-xs text-txt-tertiary truncate">{d.contact || '—'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-2.5 px-3"><TouchDots t={touch[d.id]} /></td>
                    <td className="py-2.5 px-3">
                      {STAGE_MAP[d.stage] ? <StageBadge stageKey={d.stage} /> : <span className="text-txt-tertiary">{d.stage}</span>}
                    </td>
                    <td className="py-2.5 px-3">
                      <span className="inline-flex items-center gap-1 flex-wrap">
                        <QualStatusChip status={d.qualificationStatus} />
                        <QualPctBadge pct={d.qualPct} />
                        {d.qualificationStatus === 'qualified' && d.leadGrade && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-slate-800 text-white" title="Priority grade">{d.leadGrade}</span>
                        )}
                      </span>
                    </td>
                    <td className="py-2.5 px-3"><DemoCell deal={d} /></td>
                    <td className="py-2.5 px-3"><ScoreBadge score={d.score} /></td>
                    <td className="py-2.5 px-3 text-txt-secondary whitespace-nowrap">{d.source || '—'}</td>
                    <td className="py-2.5 px-3 text-txt-tertiary whitespace-nowrap">{dateFromDaysAgo(d.createdDays)}</td>
                    <td className="py-2.5 px-4 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      {isArchivedTab ? (
                        <button onClick={() => doRestore(d)} className="text-brand-600 text-xs font-semibold hover:underline">Restore</button>
                      ) : (
                        <div className="inline-flex items-center gap-3">
                          {tab === 'to_qualify' && (
                            <>
                              {/* Dial the contact, log the attempt, then FORCE the outcome form. */}
                              {d.contactPhone ? (
                                <a
                                  href={`tel:${d.contactPhone.replace(/[^+\d]/g, '')}`}
                                  onClick={() => {
                                    addActivity(d.id, { type: 'call', body: `Qualification call started — dialled ${d.contactPhone} from the To Qualify list` }).catch(() => {})
                                    setQualSaves(0)
                                    setQualDialog({ deal: d, forced: true })
                                  }}
                                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700"
                                  title={`Call ${d.contact || d.company} — ${d.contactPhone}`}
                                >
                                  <IconPhone className="w-3 h-3" /> Call
                                </a>
                              ) : (
                                <span className="px-2.5 py-1 rounded-lg text-xs font-bold text-txt-tertiary bg-surface-tertiary cursor-not-allowed inline-flex items-center gap-1" title="No phone number on the contact — add one via the lead"><IconPhone className="w-3 h-3" /> —</span>
                              )}
                              <button
                                onClick={() => { setQualSaves(0); setQualDialog({ deal: d, forced: false }) }}
                                className="px-2.5 py-1 rounded-lg text-xs font-bold text-brand-700 border border-brand-300 hover:bg-brand-50"
                                title="Open the qualification panel"
                              >
                                ✓ Qualify
                              </button>
                            </>
                          )}
                          <QuickBookButton dealId={d.id} dealName={d.company || d.name} compact />
                          <button onClick={() => navigate(`/crm/lead/${d.id}`)} className="text-brand-600 text-xs font-semibold">Open →</button>
                          {/* Kebab quick actions (Lee, 17 Sep 2026) — same menu as the show screen. */}
                          <LeadKebab dealId={d.id} dealName={d.company || d.name} phone={d.contactPhone} onChanged={reload} />
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        </div>

        {/* Pagination — pinned below the list, 50 per page. */}
        {rows.length > PAGE_SIZE && (
          <div className="px-6 py-3 border-t border-border bg-surface flex items-center justify-between gap-3 shrink-0">
            <p className="text-xs text-txt-tertiary">Showing {from}–{to} of {rows.length}</p>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setPage(0)}
                disabled={clampedPage === 0}
                className="px-2.5 py-1.5 text-xs font-medium rounded-lg text-txt-secondary hover:bg-surface-hover disabled:opacity-40 disabled:hover:bg-transparent"
              >« First</button>
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={clampedPage === 0}
                className="px-2.5 py-1.5 text-xs font-medium rounded-lg text-txt-secondary hover:bg-surface-hover disabled:opacity-40 disabled:hover:bg-transparent"
              >‹ Prev</button>
              <span className="px-2 text-xs font-medium text-txt-primary whitespace-nowrap">Page {clampedPage + 1} of {pageCount}</span>
              <button
                onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                disabled={clampedPage >= pageCount - 1}
                className="px-2.5 py-1.5 text-xs font-medium rounded-lg text-txt-secondary hover:bg-surface-hover disabled:opacity-40 disabled:hover:bg-transparent"
              >Next ›</button>
              <button
                onClick={() => setPage(pageCount - 1)}
                disabled={clampedPage >= pageCount - 1}
                className="px-2.5 py-1.5 text-xs font-medium rounded-lg text-txt-secondary hover:bg-surface-hover disabled:opacity-40 disabled:hover:bg-transparent"
              >Last »</button>
            </div>
          </div>
        )}
      </div>
      )}


      {/* Qualification dialog. After a call (forced=true) it CANNOT close until at
          least one outcome save lands — the rep must record what they learned. */}
      {qualDialog && (() => {
        const canClose = !qualDialog.forced || qualSaves > 0
        const tryClose = () => {
          if (canClose) { setQualDialog(null); reload() }
          else crmToast('Log the call outcome first — save at least one qualification answer (or Nurture / Disqualify)', 'error')
        }
        return (
          <Overlay onClose={tryClose} width={580}>
            {/* min-h-0: lets a tall qualification panel scroll inside the 92vh cap (UI audit 21 Aug). */}
            <div className="p-4 overflow-y-auto min-h-0">
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm font-bold text-txt-primary">
                  {qualDialog.forced ? 'Call outcome — ' : 'Qualify — '}{qualDialog.deal.company || qualDialog.deal.name}
                </p>
                <button
                  onClick={tryClose}
                  className={`text-lg leading-none px-1 ${canClose ? 'text-txt-tertiary hover:text-txt-primary' : 'text-txt-tertiary/40 cursor-not-allowed'}`}
                  title={canClose ? 'Close' : 'Save the outcome first'}
                >
                  ×
                </button>
              </div>
              {qualDialog.forced && qualSaves === 0 && (
                <p className="text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 mb-2">
                  Record what you learned before closing — update the gates below, or use Nurture / Disqualify with a reason.
                </p>
              )}
              <DealQualification dealId={qualDialog.deal.id} onChanged={() => setQualSaves((n) => n + 1)} />
            </div>
          </Overlay>
        )
      })()}
      {confirm && (
        <ConfirmDialog
          title={confirm.title}
          message={confirm.message}
          confirmLabel="Archive"
          onConfirm={confirm.onConfirm}
          onClose={() => setConfirm(null)}
        />
      )}
    </div>
  )
}
